import { skipToken, useQuery } from '@tanstack/react-query';
import type { IntegrationsGetSegmentInResponse } from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';
import { integrationKeys } from '../integration/entity';

/**
 * The workspace's Segment source connection (`GET /v1/environments/{id}/integrations/segment_in`).
 *
 * The record exists only once a destination has been created, so a 404 means "not
 * connected" rather than an error, and {@link getSegmentInConnection} reports that
 * as `null`.
 */

/** `source` names the Segment source the destination was created under. */
export type SegmentInConnection = IntegrationsGetSegmentInResponse;

/**
 * Keyed under the workspace's integration namespace so `clearWorkspaceQueries`
 * sweeps it on a workspace switch, and so invalidating that namespace after a
 * connect or disconnect also moves `getSegmentInPresent`, which reads the same
 * endpoint for the integrations directory badge on a 60s stale time.
 */
export const segmentInConnectionQueryKey = (workspaceId: number) =>
  [...integrationKeys.all(workspaceId), 'segment_in', 'connection'] as const;

/**
 * Reads the connection, or `null` when the workspace has none.
 *
 * Every failure reads as "not connected", matching the Ember service, which
 * requested with `ignoreErrors` and returned `false` from its catch. A transport
 * failure is therefore indistinguishable from an absent record here — the screen
 * shows the disconnected state either way rather than an error.
 *
 * The sibling `segment-out-integration` deliberately does the opposite, because
 * there a swallowed 5xx renders a create form over a record that already exists.
 * The risk is the same here — an unreachable API offers "connect" and the create
 * is answered with `destination already exists` — and the parity choice is
 * deliberate: the screen recovers from that, and Ember behaved this way for the
 * whole life of the connector.
 */
export async function getSegmentInConnection(
  workspaceId: number,
  signal?: AbortSignal,
): Promise<SegmentInConnection | null> {
  try {
    return await hydraUiApi.integrations.getSegmentIn({
      params: { path: { environment_id: String(workspaceId) } },
      signal,
    });
  } catch {
    return null;
  }
}

type UseSegmentInConnectionOptions = {
  workspaceId: number | null;
  enabled?: boolean;
};

/**
 * Fetches without suspending; disabled until a workspace id is known.
 *
 * `staleTime: 0` because the connection changes underneath the page: connecting,
 * verifying and disconnecting all move it, and the reader is looking at the result.
 */
export function useSegmentInConnection({ workspaceId, enabled = true }: UseSegmentInConnectionOptions) {
  return useQuery({
    // No workspace is id 0, so the placeholder key cannot collide with a real
    // slot; the query is disabled until an id arrives, so it is never fetched.
    queryKey: segmentInConnectionQueryKey(workspaceId ?? 0),
    queryFn: enabled && workspaceId != null ? ({ signal }) => getSegmentInConnection(workspaceId, signal) : skipToken,
    staleTime: 0,
  });
}
